import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './RFTerminal.css';
import { FaBoxes, FaClipboardList, FaSignOutAlt, FaTerminal, FaUserCircle, FaWarehouse } from 'react-icons/fa';
import config from '../config/config';
import { apiService } from '../services/api.service';

const RFPutaway = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [license, setLicense] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOrderByLicense = async (licenseNum) => {
    try {
      setLoading(true);
      setError('');
      
      const endpoint = `${config.API_ENDPOINTS.PUTAWAY.ITEMS}/${licenseNum}`;
      const response = await apiService.get(endpoint);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to fetch order details');
      }

      if (result.data && result.data.length > 0) {
        setOrderData(result.data[0]); // Take the first item from the array
        return true;
      } else {
        setError('No items found for this license');
        return false;
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch license details');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPutaway = async (licenseNum, location) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.post(
        config.API_ENDPOINTS.PUTAWAY.CREATE,
        { license: licenseNum, locationCode: location }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit putaway');
      }

      return true;
    } catch (error) {
      setError(error.message || 'Failed to complete putaway');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = async () => {
    if (!input && step !== 3) {
      setError('Input required');
      return;
    }

    try {
      switch (step) {
        case 1:
          const success = await fetchOrderByLicense(input);
          if (success) {
            setLicense(input);
            setInput('');
            setStep(2);
          }
          break;

        case 2:
          try {
            const currentLocation = input;
            setLocationCode(currentLocation);
            
            const putawaySuccess = await handleSubmitPutaway(license, currentLocation);
            if (putawaySuccess) {
              setInput('');
              setStep(3);
            }
          } catch (error) {
            setError(error.message);
          }
          break;

        case 3:
          setStep(1);
          setInput('');
          setLicense('');
          setLocationCode('');
          setOrderData(null);
          setError('');
          navigate('/rf');
          return;
      }
    } catch (error) {
      console.error('Error in handleEnter:', error);
      setError(error.message || 'System Error');
    }
  };

  const handleKeyPress = useCallback((event) => {
    event.preventDefault();
    const key = event.key.toUpperCase();

    if (/^[0-9A-Z]$/.test(key)) {
      setInput(prev => prev + key);
      return;
    }

    switch (key) {
      case 'ENTER':
        handleEnter();
        break;
      
      case 'BACKSPACE':
        setInput(prev => prev.slice(0, -1));
        break;
      
      case 'F1':
        if (step > 1) {
          setStep(prev => prev - 1);
          setInput('');
          setError('');
        } else {
          navigate('/rf');
        }
        break;
    }
  }, [step, input, navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Debug logging
  useEffect(() => {
    console.log('Current State:', {
      step,
      input,
      license,
      locationCode,
      orderData,
      error
    });
  }, [step, input, license, locationCode, orderData, error]);

  const renderScreen = () => {
    const screens = {
      1: (
        <div className="rf-screen">
          <div className="rf-title">PUTAWAY</div>
          <div className="rf-prompt">SCAN OR ENTER LICENSE</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
          {loading && <div className="rf-loading">Loading...</div>}
          {error && (
            <div className="rf-error-message">
              {error}
              <div className="rf-error-prompt">Press Any Key</div>
            </div>
          )}
        </div>
      ),
      2: (
        <div className="rf-screen">
          <div className="rf-title">PUTAWAY</div>
          <div className="rf-info">LICENSE: {license}</div>
          {orderData && (
            <div className="rf-order-info">
              <div>ORDER: {orderData.orderNumber}</div>
              <div>ITEM: {orderData.itemNumber}</div>
              <div>CASES: {orderData.cases}</div>
              <div>CASE QTY: {orderData.caseQty}</div>
              <div>TOTAL QTY: {orderData.totalQty}</div>
              <div>RECEIVING POINT: {orderData.receivingPoint}</div>
              <div>INBOUND LOAD: {orderData.inboundLoad}</div>
            </div>
          )}
          <div className="rf-prompt">SCAN LOCATION</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
          {loading && <div className="rf-loading">Loading...</div>}
          {error && (
            <div className="rf-error-message">
              {error}
              <div className="rf-error-prompt">Press Any Key</div>
            </div>
          )}
        </div>
      ),
      3: (
        <div className="rf-screen">
          <div className="rf-title">PUTAWAY COMPLETE</div>
          <div className="rf-info">LICENSE: {license}</div>
          <div className="rf-info">LOCATION: {locationCode}</div>
          {orderData && (
            <div className="rf-order-info">
              <div>ORDER: {orderData.orderNumber}</div>
              <div>ITEM: {orderData.itemNumber}</div>
              <div>CASES: {orderData.cases}</div>
              <div>CASE QTY: {orderData.caseQty}</div>
              <div>TOTAL QTY: {orderData.totalQty}</div>
              <div>RECEIVING POINT: {orderData.receivingPoint}</div>
              <div>INBOUND LOAD: {orderData.inboundLoad}</div>
            </div>
          )}
          <div className="rf-prompt">PRESS ENTER TO CONTINUE</div>
        </div>
      )
    };

    return screens[step];
  };

  return (
    <div className="inbound-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <FaWarehouse className="sidebar-icon" />
          <span>WMS System</span>
        </div>
        <nav className="sidebar-nav">
          <Link to="/inbound-order" className="active"><FaBoxes /> Inbound Orders</Link>
          <Link to="/rf" className="active"><FaTerminal /> RF Terminal</Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Navigation */}
        <header className="top-nav">
          <div className="search-container">
            <input 
              type="text"
              placeholder="Search..."
              className="search-input"
            />
          </div>
          <div className="user-nav">
            <span className="user-name">
              <FaUserCircle /> John Doe
            </span>
            <button onClick={() => navigate('/login')} className="logout-btn">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          <div className="content-header">
            <h1>RF Putaway</h1>
          </div>
    <div className="rf-terminal" tabIndex={0}>
      {renderScreen()}
      <div className="rf-footer">
        <div>F1:BACK</div>
        <div>ENT:NEXT</div>
      </div>
    </div>
    </div>
    </div>
    </div>  
  );
};

export default RFPutaway; 